import { useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarDays } from 'lucide-react';
import type { PhotoPost } from '@/types';
import { REACTIONS } from '@/config/reactions';
import { formatCompactNumber, formatDate } from '@/lib/utils';
import { useReaction } from '@/hooks/useReaction';
import { FloatingReaction } from '@/components/ui/FloatingReaction';

interface PostCardProps {
  post: PhotoPost;
  onOpen: (post: PhotoPost) => void;
}

export const PostCard = ({ post, onOpen }: PostCardProps) => {
  const { react, isPending } = useReaction();

  const [trigger, setTrigger] = useState(0);
  const [emoji, setEmoji] = useState('❤️');

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass overflow-hidden rounded-[28px] shadow-soft"
    >
      <button
        type="button"
        onClick={() => onOpen(post)}
        className="group block w-full overflow-hidden bg-black"
        aria-label={`Open ${post.caption} in preview`}
      >
        <img
          src={post.imageUrl}
          alt={post.caption}
          loading="lazy"
          decoding="async"
          width={post.width}
          height={post.height}
          className="aspect-[4/5] w-full object-cover transition duration-300 group-hover:scale-[1.02]"
        />
      </button>

      <div className="relative space-y-4 p-4">
        <FloatingReaction
          emoji={emoji}
          trigger={trigger}
        />

        <p className="line-clamp-2 text-sm text-white/90">
          {post.caption}
        </p>

        <div className="flex items-center justify-between text-xs text-white/55">
          <span className="inline-flex items-center gap-1">
            <CalendarDays size={12} />
            {formatDate(post.createdAt)}
          </span>

          <span>
            {formatCompactNumber(post.totalReactions)} reactions
          </span>
        </div>

        <div className="flex items-center gap-3 text-2xl">
          {REACTIONS.map((reaction) => (
            <motion.button
              key={reaction.key}
              whileTap={{ scale: 1.35 }}
              type="button"
              disabled={isPending}
              onClick={() => {
                setEmoji(reaction.emoji);
                setTrigger(Date.now());

                react({
                  postId: post.id,
                  reaction: reaction.key,
                });
              }}
              className={`transition duration-200 ${
                post.userReaction === reaction.key
                  ? 'scale-125 opacity-100'
                  : 'grayscale opacity-40 hover:grayscale-0 hover:opacity-100 hover:scale-110'
              } disabled:opacity-40`}
            >
              {reaction.emoji}
            </motion.button>
          ))}
        </div>
      </div>
    </motion.article>
  );
};